import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AuthContext } from "@/App";
import { toast } from "sonner";
import { Key, Copy, Check, Plus, Trash2, RefreshCw, Clock, Activity, Code, Terminal, Eye, EyeOff, Shield, Settings as SettingsIcon } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SNIPPETS = {
  python: (key) => `from openai import OpenAI

client = OpenAI(
    api_key="${key}",
    base_url="https://api.qnneal.dev/v1"
)

response = client.chat.completions.create(
    model="gpt-4o",
    messages=[{"role": "user", "content": "Hello!"}]
)
print(response.choices[0].message.content)`,
  curl: (key) => `curl https://api.qnneal.dev/v1/chat/completions \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"gpt-4o","messages":[{"role":"user","content":"Hello!"}]}'`,
};

export default function APIKeysTab() {
  const { user } = useContext(AuthContext);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyPerms, setNewKeyPerms] = useState("full"); // 'full' | 'restricted' | 'read_only'
  const [newKeyScopes, setNewKeyScopes] = useState([
    // Sensible default for restricted: route LLM requests
    "LLM-Gateway/chat",
  ]);
  const [newKeyProject, setNewKeyProject] = useState(""); // "" = all projects
  const [newKeyOwner, setNewKeyOwner] = useState("user"); // 'user' | 'service_account'
  const [newKeyExpiry, setNewKeyExpiry] = useState("never"); // 'never' | '30d' | '90d'
  const [newKeyRpm, setNewKeyRpm] = useState("");
  const [newKeySpendCap, setNewKeySpendCap] = useState("");
  const [scopeCatalog, setScopeCatalog] = useState([]);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState("");
  const [editingKey, setEditingKey] = useState(null); // {key_preview, name, ...}

  const isDemo = user?.is_demo;

  // Project options — derived from existing keys + a small static list.
  // Avoids needing a new backend table for now.
  const projectOptions = useMemo(() => {
    const set = new Set(["HAVEN", "Vista Portfolio", "H&F Portfolio", "XIUS Integration"]);
    keys.forEach(k => { if (k.project) set.add(k.project); });
    return Array.from(set);
  }, [keys]);

  const fetchKeys = async () => {
    try {
      const [keysRes, scopesRes] = await Promise.all([
        axios.get(`${API}/keys`, { withCredentials: true }),
        axios.get(`${API}/keys/scopes`, { withCredentials: true }).catch(() => ({ data: { scopes: [] } })),
      ]);
      setKeys(keysRes.data.keys || []);
      setScopeCatalog(scopesRes.data.scopes || []);
    } catch (_e) { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { fetchKeys(); }, []);

  const resetCreateForm = useCallback(() => {
    setNewKeyName("");
    setNewKeyPerms("full");
    setNewKeyScopes(["LLM-Gateway/chat"]);
    setNewKeyProject("");
    setNewKeyOwner("user");
    setNewKeyExpiry("never");
    setNewKeyRpm("");
    setNewKeySpendCap("");
  }, []);

  const expiryToIso = (choice) => {
    if (choice === "never" || !choice) return null;
    const days = choice === "30d" ? 30 : choice === "90d" ? 90 : null;
    if (!days) return null;
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString();
  };

  const handleCreate = async () => {
    if (isDemo) { toast.error("Create an account to generate API keys"); return; }
    if (newKeyPerms === "restricted" && newKeyScopes.length === 0) {
      toast.error("Restricted keys require at least one scope"); return;
    }
    setCreating(true);
    try {
      const body = {
        name: newKeyName.trim() || "Untitled Key",
        permissions: newKeyPerms,
        scopes: newKeyPerms === "restricted" ? newKeyScopes : null,
        project: newKeyProject || null,
        owner_type: newKeyOwner,
        expires_at: expiryToIso(newKeyExpiry),
        rate_limit_rpm: newKeyRpm ? Number(newKeyRpm) : null,
        monthly_spend_cap: newKeySpendCap ? Number(newKeySpendCap) : null,
      };
      const res = await axios.post(`${API}/keys/create`, body, { withCredentials: true });
      setCreatedKey(res.data);
      resetCreateForm();
      setShowCreate(false);
      await fetchKeys();
      toast.success("API key created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create key");
    }
    setCreating(false);
  };

  const toggleScope = (scopeId) => {
    setNewKeyScopes(prev =>
      prev.includes(scopeId) ? prev.filter(s => s !== scopeId) : [...prev, scopeId]
    );
  };

  const handleSavePermissions = async () => {
    if (!editingKey) return;
    try {
      const body = {
        key_preview: editingKey.key_preview,
        permissions: editingKey.permissions,
        scopes: editingKey.permissions === "restricted" ? (editingKey.scopes || []) : null,
        project: editingKey.project || null,
      };
      await axios.put(`${API}/keys/permissions`, body, { withCredentials: true });
      toast.success("Permissions updated");
      setEditingKey(null);
      await fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update permissions");
    }
  };

  const handleCopy = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success("Copied!");
    setTimeout(() => setCopied(""), 3000);
  };

  const handleRevoke = async (preview) => {
    if (!confirm("Revoke this API key? It will stop working immediately.")) return;
    try {
      await axios.post(`${API}/keys/revoke`, { key_preview: preview }, { withCredentials: true });
      toast.success("Key revoked");
      await fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke");
    }
  };

  const handleRegenerate = async (preview) => {
    if (!confirm("Regenerate this key? The old key will stop working immediately.")) return;
    try {
      const res = await axios.post(`${API}/keys/regenerate`, { key_preview: preview }, { withCredentials: true });
      setCreatedKey(res.data);
      await fetchKeys();
      toast.success("Key regenerated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to regenerate");
    }
  };

  const activeKeys = keys.filter(k => k.is_active);
  const revokedKeys = keys.filter(k => !k.is_active);

  return (
    <div className="space-y-6" data-testid="api-keys-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight">API Keys</h2>
          <p className="text-sm text-[var(--qn-foreground-muted)]">
            Your API keys let your apps connect to QNNEAL. Keep them secret — treat them like passwords.
          </p>
        </div>
        {!isDemo && (
          <Button onClick={() => setShowCreate(true)} className="bg-[var(--qn-primary)]" data-testid="create-key-btn">
            <Plus className="w-4 h-4 mr-1" /> Create API Key
          </Button>
        )}
      </div>

      {isDemo && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4 text-center text-sm text-amber-300">
            Demo mode — create an account to generate your own API keys
          </CardContent>
        </Card>
      )}

      {/* Key Created Modal */}
      <AnimatePresence>
        {createdKey && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
            <Card className="border-emerald-500/30 bg-emerald-500/5" data-testid="created-key-card">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2 text-emerald-400">
                  <Check className="w-5 h-5" />
                  <span className="font-semibold">Your API Key is Ready!</span>
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-xs p-2 rounded">
                  Copy this key now. You won&apos;t be able to see it again.
                </div>
                <div className="bg-black/20 rounded-lg p-3 border border-white/10">
                  <code className="text-sm font-mono text-emerald-300 break-all" data-testid="new-key-display">
                    {createdKey.full_key}
                  </code>
                </div>
                <Button
                  onClick={() => handleCopy(createdKey.full_key, "new")}
                  className={`w-full py-5 text-base ${copied === "new" ? "bg-emerald-600" : "bg-[var(--qn-primary)]"}`}
                  data-testid="copy-new-key"
                >
                  {copied === "new" ? <><Check className="w-4 h-4 mr-2" /> COPIED!</> : <><Copy className="w-4 h-4 mr-2" /> COPY API KEY</>}
                </Button>

                <details className="text-sm">
                  <summary className="cursor-pointer text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]">Quick Start Code</summary>
                  <pre className="mt-2 bg-black/30 rounded p-3 text-xs font-mono text-emerald-300 whitespace-pre-wrap overflow-x-auto border border-white/10">
                    {SNIPPETS.python(createdKey.key_preview)}
                  </pre>
                </details>

                <Button variant="outline" onClick={() => setCreatedKey(null)} className="w-full border-white/10" data-testid="dismiss-key-card">
                  I&apos;ve copied my key
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Key Form — OpenAI-style modal layout */}
      <AnimatePresence>
        {showCreate && !createdKey && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
            <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="create-key-form">
              <CardContent className="p-5 space-y-4">
                {/* Owned by */}
                <div>
                  <label className="text-sm font-medium">Owned by</label>
                  <div className="flex gap-2 mt-1.5">
                    {[
                      { id: "user", label: "You" },
                      { id: "service_account", label: "Service account" },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setNewKeyOwner(opt.id)}
                        data-testid={`owner-${opt.id}`}
                        className={`px-4 py-1.5 rounded text-sm border transition ${newKeyOwner === opt.id ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]/10 text-[var(--qn-primary)]" : "border-[var(--qn-border)] text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[var(--qn-foreground-muted)] mt-2 leading-relaxed">
                    This API key is tied to your {newKeyOwner === "user" ? "user" : "service account"} and can make requests against the selected project. If you are removed from the organization or project, this key will be disabled.
                  </p>
                </div>

                {/* Name */}
                <div>
                  <label className="text-sm font-medium">Name <span className="text-[var(--qn-foreground-muted)] font-normal">Optional</span></label>
                  <Input value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="e.g. LintelAQ, Production Backend" className="mt-1 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="new-key-name" autoFocus />
                </div>

                {/* Project */}
                <div>
                  <label className="text-sm font-medium">Project</label>
                  <select
                    value={newKeyProject} onChange={e => setNewKeyProject(e.target.value)}
                    data-testid="new-key-project"
                    className="mt-1 w-full px-3 py-2 bg-[var(--qn-background)] border border-[var(--qn-border)] rounded text-sm text-[var(--qn-foreground)] focus:outline-none focus:border-[var(--qn-primary)]"
                  >
                    <option value="">All Projects</option>
                    {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>

                {/* Permissions */}
                <div>
                  <label className="text-sm font-medium">Permissions</label>
                  <div className="flex gap-2 mt-1.5">
                    {[
                      { id: "full", label: "All" },
                      { id: "restricted", label: "Restricted" },
                      { id: "read_only", label: "Read only" },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setNewKeyPerms(opt.id)}
                        data-testid={`perm-${opt.id}`}
                        className={`px-4 py-1.5 rounded text-sm border transition ${newKeyPerms === opt.id ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]/10 text-[var(--qn-primary)]" : "border-[var(--qn-border)] text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Restricted scope checkboxes */}
                {newKeyPerms === "restricted" && (
                  <div className="border border-[var(--qn-border)] rounded-lg p-3 bg-[var(--qn-background)]" data-testid="scope-checkboxes">
                    <p className="text-xs text-[var(--qn-foreground-muted)] mb-2">Select scopes for this key. <span className="text-[var(--qn-primary)]">LLM-Gateway/chat</span> is the core scope for routing chat requests.</p>
                    <div className="space-y-1.5 max-h-56 overflow-y-auto">
                      {(scopeCatalog.length ? scopeCatalog : [
                        { id: "LLM-Gateway/chat", label: "LLM-Gateway / chat", description: "Route chat completion requests" },
                      ]).map(s => (
                        <label key={s.id} className="flex items-start gap-2 cursor-pointer hover:bg-[var(--qn-card)] p-1.5 rounded" data-testid={`scope-${s.id}`}>
                          <input
                            type="checkbox"
                            checked={newKeyScopes.includes(s.id)}
                            onChange={() => toggleScope(s.id)}
                            className="mt-0.5 accent-[var(--qn-primary)]"
                          />
                          <div className="flex-1">
                            <div className="text-sm">{s.label}</div>
                            <div className="text-xs text-[var(--qn-foreground-muted)]">{s.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rate limits */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[var(--qn-foreground-muted)]">Requests/min (optional)</label>
                    <Input type="number" value={newKeyRpm} onChange={e => setNewKeyRpm(e.target.value)} placeholder="unlimited" className="mt-1 bg-[var(--qn-background)] border-[var(--qn-border)] text-sm" data-testid="new-key-rpm" />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--qn-foreground-muted)]">Monthly spend cap (USD)</label>
                    <Input type="number" value={newKeySpendCap} onChange={e => setNewKeySpendCap(e.target.value)} placeholder="unlimited" className="mt-1 bg-[var(--qn-background)] border-[var(--qn-border)] text-sm" data-testid="new-key-spend-cap" />
                  </div>
                </div>

                {/* Expiration */}
                <div>
                  <label className="text-sm font-medium">Expiration</label>
                  <div className="flex gap-2 mt-1.5">
                    {[
                      { id: "never", label: "Never" },
                      { id: "30d", label: "30 days" },
                      { id: "90d", label: "90 days" },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setNewKeyExpiry(opt.id)}
                        data-testid={`expiry-${opt.id}`}
                        className={`px-4 py-1.5 rounded text-sm border transition ${newKeyExpiry === opt.id ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]/10 text-[var(--qn-primary)]" : "border-[var(--qn-border)] text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={handleCreate} disabled={creating} className="bg-[var(--qn-primary)]" data-testid="confirm-create-key">
                    <Key className="w-4 h-4 mr-1" /> {creating ? "Creating..." : "Create secret key"}
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowCreate(false); resetCreateForm(); }}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Keys */}
      {activeKeys.length > 0 && (
        <div className="space-y-3">
          {activeKeys.map((k, i) => {
            const scopes = Array.isArray(k.scopes) ? k.scopes : ["*"];
            const isFull = scopes.includes("*");
            const isEditing = editingKey?.key_preview === k.key_preview;
            return (
            <motion.div key={k.key_preview} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid={`api-key-${k.key_preview}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Key className="w-4 h-4 text-[var(--qn-primary)]" />
                        <span className="font-medium text-sm">{k.name}</span>
                        <Badge className="text-[9px]" variant="outline">{k.permissions || "full"}</Badge>
                        {k.project && (
                          <Badge className="text-[9px] bg-purple-500/10 text-purple-300 border-purple-500/30" variant="outline">
                            {k.project}
                          </Badge>
                        )}
                        {k.owner_type === "service_account" && (
                          <Badge className="text-[9px] bg-blue-500/10 text-blue-300 border-blue-500/30" variant="outline">
                            service account
                          </Badge>
                        )}
                      </div>
                      <div className="font-mono text-xs text-[var(--qn-foreground-muted)] mt-1">{k.key_preview}</div>

                      {/* Scope badges */}
                      <div className="flex flex-wrap gap-1 mt-2" data-testid={`scopes-${k.key_preview}`}>
                        {isFull ? (
                          <Badge className="text-[10px] bg-emerald-500/10 text-emerald-300 border-emerald-500/30" variant="outline">
                            <Shield className="w-2.5 h-2.5 mr-1" /> All scopes
                          </Badge>
                        ) : (
                          scopes.map(s => (
                            <Badge key={s} className="text-[10px] bg-teal-500/10 text-teal-300 border-teal-500/30" variant="outline">
                              {s}
                            </Badge>
                          ))
                        )}
                      </div>

                      <div className="flex items-center gap-4 mt-2 text-xs text-[var(--qn-foreground-muted)] flex-wrap">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Created {new Date(k.created_at).toLocaleDateString()}</span>
                        {k.last_used_at && <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> Last used {new Date(k.last_used_at).toLocaleString()}</span>}
                        <span>{k.request_count?.toLocaleString() || 0} requests</span>
                        {k.expires_at && <span>Expires {new Date(k.expires_at).toLocaleDateString()}</span>}
                        {k.rate_limit_rpm && <span>{k.rate_limit_rpm} rpm</span>}
                        {k.monthly_spend_cap && <span>${k.monthly_spend_cap}/mo cap</span>}
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <Button variant="ghost" size="sm"
                        onClick={() => setEditingKey(isEditing ? null : { ...k, scopes: [...scopes] })}
                        className="text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)]"
                        data-testid={`edit-perms-${k.key_preview}`}
                        title="Edit permissions"
                      >
                        <SettingsIcon className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleRegenerate(k.key_preview)} className="text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)]" data-testid={`regenerate-${k.key_preview}`}>
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleRevoke(k.key_preview)} className="text-[var(--qn-foreground-muted)] hover:text-red-400" data-testid={`revoke-${k.key_preview}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Inline edit-permissions panel */}
                  {isEditing && editingKey && (
                    <div className="mt-4 pt-4 border-t border-[var(--qn-border)] space-y-3" data-testid={`edit-panel-${k.key_preview}`}>
                      <p className="text-xs text-[var(--qn-foreground-muted)]">
                        Edit permissions for <span className="text-[var(--qn-foreground)] font-mono">{k.key_preview}</span>
                      </p>
                      <div>
                        <label className="text-xs font-medium">Permission level</label>
                        <div className="flex gap-2 mt-1">
                          {[
                            { id: "full", label: "All" },
                            { id: "restricted", label: "Restricted" },
                            { id: "read_only", label: "Read only" },
                          ].map(opt => (
                            <button key={opt.id}
                              onClick={() => setEditingKey({ ...editingKey, permissions: opt.id })}
                              data-testid={`edit-perm-${opt.id}-${k.key_preview}`}
                              className={`px-3 py-1 rounded text-xs border transition ${editingKey.permissions === opt.id ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]/10 text-[var(--qn-primary)]" : "border-[var(--qn-border)] text-[var(--qn-foreground-muted)]"}`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {editingKey.permissions === "restricted" && (
                        <div className="border border-[var(--qn-border)] rounded p-2 bg-[var(--qn-background)]">
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {scopeCatalog.map(s => (
                              <label key={s.id} className="flex items-start gap-2 cursor-pointer p-1 hover:bg-[var(--qn-card)] rounded">
                                <input type="checkbox"
                                  checked={(editingKey.scopes || []).includes(s.id)}
                                  onChange={() => {
                                    const cur = editingKey.scopes || [];
                                    setEditingKey({
                                      ...editingKey,
                                      scopes: cur.includes(s.id) ? cur.filter(x => x !== s.id) : [...cur, s.id],
                                    });
                                  }}
                                  className="mt-0.5 accent-[var(--qn-primary)]"
                                  data-testid={`edit-scope-${s.id}-${k.key_preview}`}
                                />
                                <div className="text-xs">
                                  <div>{s.label}</div>
                                  <div className="text-[var(--qn-foreground-muted)]">{s.description}</div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="text-xs font-medium">Project</label>
                        <select
                          value={editingKey.project || ""}
                          onChange={e => setEditingKey({ ...editingKey, project: e.target.value || null })}
                          data-testid={`edit-project-${k.key_preview}`}
                          className="mt-1 w-full px-2 py-1 bg-[var(--qn-background)] border border-[var(--qn-border)] rounded text-xs"
                        >
                          <option value="">All Projects</option>
                          {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSavePermissions} className="bg-[var(--qn-primary)]" data-testid={`save-perms-${k.key_preview}`}>
                          Save changes
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingKey(null)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
            );
          })}
        </div>
      )}

      {activeKeys.length === 0 && !createdKey && !showCreate && !loading && !isDemo && (
        <Card className="border-[var(--qn-border)] border-dashed bg-transparent">
          <CardContent className="p-8 text-center">
            <Key className="w-10 h-10 mx-auto text-[var(--qn-foreground-muted)] mb-3" />
            <p className="text-sm text-[var(--qn-foreground-muted)]">No API keys yet. Create one to get started.</p>
            <Button onClick={() => setShowCreate(true)} className="mt-3 bg-[var(--qn-primary)]" data-testid="empty-create-key">
              <Plus className="w-4 h-4 mr-1" /> Create Your First API Key
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Revoked Keys */}
      {revokedKeys.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]">
            {revokedKeys.length} revoked key(s)
          </summary>
          <div className="mt-2 space-y-2 opacity-50">
            {revokedKeys.map(k => (
              <div key={k.key_preview} className="flex items-center gap-2 text-xs text-[var(--qn-foreground-muted)] p-2 rounded border border-[var(--qn-border)]">
                <Key className="w-3 h-3" />
                <span className="line-through">{k.name}</span>
                <span className="font-mono">{k.key_preview}</span>
                <span>Revoked {k.revoked_at ? new Date(k.revoked_at).toLocaleDateString() : ""}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <p className="text-xs text-[var(--qn-foreground-muted)]">
        Use different keys for different apps so you can track usage and revoke access independently.
      </p>
    </div>
  );
}
